import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Button, 
  Space, 
  message, 
  Spin,
  Modal,
  InputNumber,
  Select,
  List,
  Tag,
  DatePicker,
  Divider,
  Upload,
  Row,
  Col
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FileOutlined,
  UploadOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { lawService } from '../../services/lawService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { Law, LawCategory, LawStatus, Attachment } from '../../types';
import { applyStyle, removeStyle, insertImage } from '../../utils/styleUtils';
import { insertTable } from '../../utils/tableUtils';
import dayjs from 'dayjs';
import '../../styles/contentStyles.css';
import { Form as AntdForm } from 'antd';



const { Title } = Typography;
const { Option } = Select;



const LawEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [law, setLaw] = useState<Law | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);

  
  const [form] = Form.useForm();
  const category = AntdForm.useWatch('category', form);
  
  // 에디터 관련 상태
  const contentRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef<string>('');
  
  // 표 삽입 관련 상태
  const [tableModalVisible, setTableModalVisible] = useState(false);
  const [tableRows, setTableRows] = useState(2);
  const [tableCols, setTableCols] = useState(2);
  
  // 이미지 관련 상태
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageWidth, setImageWidth] = useState(300);
  const [imageHeight, setImageHeight] = useState(200);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageResizeModalVisible, setImageResizeModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  

  
  // 일반적인 표 삽입 함수
  const handleInsertTable = () => {
    const updatedContent = insertTable(contentRef, tableRows, tableCols);
    // 비동기적으로 폼 값 업데이트하여 순환 참조 방지
    setTimeout(() => {
      form.setFieldValue('content', updatedContent);
    }, 0);
    setTableModalVisible(false);
  };
  
  // 표 관련 상태, 함수, 버튼, 모달, 핸들러 등 모두 삭제


  
  // Form 필드 설정 및 contentEditable div 초기화
  useEffect(() => {
    if (law) {
      // 수정 모드: law 데이터로 Form 필드 설정
      form.setFieldsValue({
        title: law.title,
        content: law.content,
        category: law.category,
        status: law.status,
        resolution_date: law.resolution_date ? dayjs(law.resolution_date) : undefined,
        owner: law.owner || '',
        serial_number: law.serial_number || undefined,
      });

      // Form 설정 후 contentEditable div 초기화
      setTimeout(() => {
        if (contentRef.current && law.content) {
          contentRef.current.innerHTML = law.content;
          lastContentRef.current = law.content;
        }
      }, 0);
    } else if (!id || id === 'create') {
      // 생성 모드: 빈 내용으로 초기화
      if (contentRef.current) {
        contentRef.current.innerHTML = '';
        lastContentRef.current = '';
      }
    }
  }, [law, form, id]);

  // 추가 안전장치: contentEditable div가 준비되지 않은 경우를 위한 fallback
  useEffect(() => {
    if (law && contentRef.current && !contentRef.current.innerHTML && law.content) {
      contentRef.current.innerHTML = law.content;
      lastContentRef.current = law.content;
    }
  }, [law]);

  const fetchLaw = useCallback(async () => {
    if (!id || id === 'create') return;
    
    setLoading(true);
    try {
      const data = await lawService.getLaw(id);

      setLaw(data);
      
      // 첨부파일도 함께 조회
      try {
        const attachmentData = await attachmentService.getLawAttachments(id);
        setAttachments(attachmentData);
      } catch (error) {

        setAttachments(data.attachments || []);
      }
    } catch (error: any) {
      
      message.error('규범류를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && id !== 'create') {
      fetchLaw();
    } else {
      // 생성 모드인 경우 로딩 상태를 false로 설정
      setLoading(false);
    }
  }, [fetchLaw, id]);

  // 표 행 추가/삭제 버튼 동작을 위한 직접 이벤트 리스너 등록
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      if (target.classList.contains('add-row-btn')) {
        e.preventDefault();
        e.stopPropagation();
        
        const controlsDiv = target.closest('.table-controls');
        if (!controlsDiv) return;
        
        const table = controlsDiv.previousElementSibling as HTMLTableElement;
        if (!table) return;
        
        const tbody = table.querySelector('tbody');
        if (!tbody) return;
        
        // 개정사항 표인지 일반적인 표인지 확인
        const isAmendmentTable = table.classList.contains('amendment-table') || 
                                table.closest('.amendment-table');
        
        if (isAmendmentTable) {
          // 개정사항 표 (2열 고정)
          const newRow = document.createElement('tr');
          newRow.innerHTML = `
            <td contenteditable="true"></td>
            <td contenteditable="true"></td>
          `;
          tbody.appendChild(newRow);
        } else {
          // 일반적인 표 (동적 열 수)
          const firstRow = tbody.querySelector('tr');
          if (firstRow) {
            const colCount = firstRow.children.length;
            const newRow = document.createElement('tr');
            let rowHTML = '';
            for (let i = 0; i < colCount; i++) {
              rowHTML += '<td contenteditable="true"></td>';
            }
            newRow.innerHTML = rowHTML;
            tbody.appendChild(newRow);
          }
        }
        
        // 폼 값 업데이트
        if (contentRef.current) {
          form.setFieldValue('content', contentRef.current.innerHTML);
        }
      } else if (target.classList.contains('delete-row-btn')) {
        e.preventDefault();
        e.stopPropagation();
        
        const controlsDiv = target.closest('.table-controls');
        if (!controlsDiv) return;
        
        const table = controlsDiv.previousElementSibling as HTMLTableElement;
        if (!table) return;
        
        const tbody = table.querySelector('tbody');
        if (!tbody || tbody.children.length <= 1) return;
        
        const lastChild = tbody.lastElementChild;
        if (lastChild) {
          tbody.removeChild(lastChild);
          
          // 폼 값 업데이트
          if (contentRef.current) {
            form.setFieldValue('content', contentRef.current.innerHTML);
          }
        }
      }
    };
    
    // document에 이벤트 리스너 등록 (더 안정적)
    document.addEventListener('click', handler);
    
    return () => {
      document.removeEventListener('click', handler);
    };
  }, [form]);

  // 권한 확인
  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('common.noPermission')}</Title>
        <Button type="primary" onClick={() => navigate('/laws')}>
          {t('laws.actions.backToList')}
        </Button>
      </div>
    );
  }

  const handleSave = async (values: { 
    title: string; 
    content: string; 
    category: LawCategory;
    status: LawStatus;
    resolution_date?: any;
    owner?: string;
    serial_number?: number;
  }) => {
    setSaving(true);
    try {
      const saveData = {
        ...values,
        resolution_date: values.resolution_date ? values.resolution_date.format('YYYY-MM-DD') : undefined,
        owner: values.owner || undefined,
        serial_number: values.serial_number || undefined
      };

      if (law) {
        // 기존 규범류 수정
        await lawService.updateLaw(law.law_id, saveData);
        message.success(t('laws.updateSuccess'));
        navigate(`/laws/${law.law_id}`);
      } else {
        // 새 규범류 생성
        await lawService.createLaw(saveData);
        message.success(t('laws.createSuccess'));
        navigate('/laws');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || t('laws.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (law) {
      navigate(`/laws/${law.law_id}`);
    } else {
      navigate('/laws');
    }
  };

  // 첨부파일 관련 핸들러
  const handleFileUpload = async (file: File) => {
    if (!law?.law_id) {
      message.error(t('laws.uploadAfterCreate'));
      return false;
    }

    setUploading(true);
    try {
      await attachmentService.uploadAttachment({ file, lawId: law.law_id });
      message.success(t('laws.uploadSuccess'));
      
      // 첨부파일 변경으로 인한 버전 이력 생성
      try {
        await lawService.createVersionForAttachmentsChange(law.law_id);

      } catch (versionError) {
        
      }
      
      // 첨부파일 목록 새로고침
      const updatedAttachments = await attachmentService.getLawAttachments(law.law_id);
      setAttachments(updatedAttachments);
      
      return false; // antd Upload의 자동 업로드 방지
    } catch (error: any) {
      message.error(error.response?.data?.message || t('laws.uploadFailed'));
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleFileRemove = async (attachment: Attachment) => {
    try {
      await attachmentService.deleteAttachment(attachment.attachment_id);
      message.success(t('laws.deleteAttachmentSuccess'));
      
      // 첨부파일 변경으로 인한 버전 이력 생성
      if (law?.law_id) {
        try {
          await lawService.createVersionForAttachmentsChange(law.law_id);
  
        } catch (versionError) {

        }
      }
      
      // 첨부파일 목록에서 제거
      setAttachments(prev => prev.filter(att => att.attachment_id !== attachment.attachment_id));
    } catch (error: any) {
      message.error(error.response?.data?.message || t('laws.deleteAttachmentFailed'));
    }
  };

  const handleFileDownload = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, decodeURIComponent(attachment.file_name));
    } catch (error: any) {
      message.error(t('laws.downloadFailed'));
    }
  };

  // 이미지 삽입 함수
  const handleInsertImage = async () => {
    if (!imageUrl.trim() && !imageFile) {
      message.error(t('laws.imageUrlOrFileRequired'));
      return;
    }

    // 이미지 파일이 있는 경우 서버에 업로드
    if (imageFile) {
      try {
        if (!law?.law_id) {
          message.error(t('laws.uploadAfterCreate'));
          return;
        }

        const uploadedImage = await attachmentService.uploadEditorImage(
          imageFile, 
          'law', 
          law.law_id
        );
        
        // 업로드된 이미지 URL을 사용하여 삽입
        insertImage(uploadedImage.file_path, form, contentRef);
        message.success(t('laws.imageUploadSuccess'));
      } catch (error: any) {
        message.error(error.response?.data?.message || t('laws.imageUploadFailed'));
        return;
      }
    } else {
      // URL 이미지 삽입
      insertImage(imageUrl, form, contentRef);
    }
    
    setImageModalVisible(false);
    setImageUrl('');
    setImageFile(null);
    setImagePreview('');
  };

  // 이미지 파일 변경 핸들러
  const handleImageFileChange = (file: File) => {
    setImageFile(file);
    setImageUrl('');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  // 이미지 파일 제거 핸들러
  const handleImageFileRemove = () => {
    setImageFile(null);
    setImagePreview('');
  };



  // 이미지 크기 조절 핸들러
  const handleImageResize = () => {
    if (!selectedImage) return;
    
    selectedImage.style.width = `${imageWidth}px`;
    selectedImage.style.height = `${imageHeight}px`;
    selectedImage.style.maxWidth = '100%';
    
    // form 업데이트
    const contentElement = contentRef.current;
    if (contentElement) {
      form.setFieldValue('content', contentElement.innerHTML);
    }
    
    message.success(t('laws.imageResized'));
    
    // 모달 닫기
    setImageResizeModalVisible(false);
    setSelectedImage(null);
  };

  // 자동 크기 조절 핸들러
  const handleAutoResize = () => {
    if (!selectedImage) return;
    
    const maxWidth = 800;
    const maxHeight = 600;
    
    let width = selectedImage.naturalWidth;
    let height = selectedImage.naturalHeight;
    
    // 비율을 유지하면서 크기 조정
    if (width > maxWidth) {
      const ratio = maxWidth / width;
      width = maxWidth;
      height = height * ratio;
    }
    
    if (height > maxHeight) {
      const ratio = maxHeight / height;
      height = maxHeight;
      width = width * ratio;
    }
    
    // 이미지가 최대 크기보다 작을 때는 원본 크기 유지하되 비율은 보장
    selectedImage.style.width = `${width}px`;
    selectedImage.style.height = `${height}px`;
    selectedImage.style.maxWidth = '100%';
    
    // form 업데이트
    const contentElement = contentRef.current;
    if (contentElement) {
      form.setFieldValue('content', contentElement.innerHTML);
    }
    
    message.success(t('laws.imageAutoResized'));
    
    // 모달 닫기
    setImageResizeModalVisible(false);
    setSelectedImage(null);
  };





  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('laws.loading')}</div>
      </div>
    );
  }

  return (
    <div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ marginRight: 16 }}
          >
            {t('common.cancel')}
          </Button>
            <Title level={3} style={{ margin: 0 }}>
              {law ? t('laws.editTitle') : t('laws.createTitle')}
            </Title>
          </div>
          
          <Space>
            {law && (
              <Button 
                onClick={() => {
                  if (contentRef.current && law.content) {
                    contentRef.current.innerHTML = law.content;
                    message.success(t('laws.contentReloaded'));
                  }
                }}
                title={t('laws.contentReloadHint')}
              >
                {t('laws.reloadContent')}
              </Button>
            )}
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={() => {
                // contentEditable div의 내용을 폼에 반영
                if (contentRef.current) {
                  form.setFieldValue('content', contentRef.current.innerHTML);
                }
                form.submit();
              }}
              loading={saving}
            >
              {t('common.save')}
            </Button>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            title: '',
            content: '',
            category: LawCategory.KYOGYU,
            status: LawStatus.ENACTED,
            resolution_date: undefined,
            owner: '',
            serial_number: undefined,
          }}
          disabled={loading}
        >
          <Form.Item
            name="title"
            label={t('laws.title')}
            rules={[{ required: true, message: t('laws.titleRequired') }]}
          >
            <Input placeholder={t('laws.titlePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="category"
            label={t('laws.category')}
            rules={[{ required: true, message: t('laws.categoryRequired') }]}
          >
            <Select placeholder={t('laws.categoryPlaceholder')}>
              <Option value={LawCategory.KYOHUN}>{t('laws.kyohun')}</Option>
              <Option value={LawCategory.KYOGYU}>{t('laws.kyogyu')}</Option>
              <Option value={LawCategory.KYORYEONG}>{t('laws.kyoryeong')}</Option>
              <Option value={LawCategory.YEGYU}>{t('laws.yegyu')}</Option>
            </Select>
          </Form.Item>

          {/* 관리처 입력란: 분류가 '예규'일 때만 노출, 아니면 히든 */}
          {category === LawCategory.YEGYU ? (
            <Form.Item
              name="owner"
              label={t('laws.detail.labels.owner')}
            >
              <Input placeholder={t('laws.detail.labels.ownerPlaceholder')}/>
            </Form.Item>
          ) : (
            <Form.Item name="owner" style={{ display: 'none' }}>
              <Input type="hidden" />
            </Form.Item>
          )}

          <Form.Item
            name="status"
            label={t('laws.status')}
            rules={[{ required: true, message: t('laws.statusRequired') }]}
          >
            <Select placeholder={t('laws.statusPlaceholder')}>
              <Option value={LawStatus.ENACTED}>{t('laws.enacted')}</Option>
              <Option value={LawStatus.AMENDED}>{t('laws.amended')}</Option>
              <Option value={LawStatus.FULLY_AMENDED}>{t('laws.fullyAmended')}</Option>
              <Option value={LawStatus.ABOLISHED}>{t('laws.abolished')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="resolution_date"
            label={t('laws.resolutionDate')}
          >
            <DatePicker 
              format="YYYY-MM-DD"
              placeholder={t('laws.resolutionDatePlaceholder')}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="serial_number"
            label={t('laws.serialNumber')}
          >
            <InputNumber 
              placeholder={t('laws.serialNumberPlaceholder')} 
              style={{ width: '100%' }} 
              min={1}
            />
          </Form.Item>

          <Form.Item
            name="content"
            label={t('editor.content')}
            rules={[{ required: true, message: t('editor.contentRequired') }]}
          >
            {/* 스타일 툴바 - 내용 영역 바로 위에 위치 */}
            <div style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderBottom: 'none',
              borderRadius: '6px 6px 0 0',
              padding: '12px',
              marginBottom: 0,
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {/* 텍스트 스타일 버튼들 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('진하게', form, contentRef)}
              title={t('editor.bold')}
            >
              {t('editor.bold')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('왼쪽', form, contentRef)}
              title={t('editor.alignLeft')}
            >
              {t('editor.alignLeft')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('가운데', form, contentRef)}
              title={t('editor.alignCenter')}
            >
              {t('editor.alignCenter')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('오른쪽', form, contentRef)}
              title={t('editor.alignRight')}
            >
              {t('editor.alignRight')}
            </Button>
            
            {/* 구분선 */}
            <div style={{ width: '1px', backgroundColor: '#d9d9d9', margin: '0 4px', height: '20px' }} />
            
            {/* 법률 문서 스타일 버튼들 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('제목', form, contentRef)}
              title={t('editor.titleStyle')}
            >
              {t('editor.title')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('장', form, contentRef)}
              title={t('editor.chapterStyle')}
            >
              {t('editor.chapter')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('조', form, contentRef)}
              title={t('editor.articleStyle')}
            >
              {t('editor.article')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('항', form, contentRef)}
              title={t('editor.paragraphStyle')}
            >
              {t('editor.paragraph')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('호', form, contentRef)}
              title={t('editor.subParagraphStyle')}
            >
              {t('editor.subParagraph')}
            </Button>
            <Button 
              size="small" 
              danger
              onClick={() => removeStyle(form, contentRef)}
              title={t('editor.removeStyle')}
            >
              {t('editor.removeStyle')}
            </Button>
            
            {/* 구분선 */}
            <div style={{ width: '1px', backgroundColor: '#d9d9d9', margin: '0 4px', height: '20px' }} />
            
            {/* 표 삽입 버튼 */}
            <Button 
              size="small" 
              onClick={() => setTableModalVisible(true)}
              title={t('editor.insertTable')}
            >
              {t('editor.table')}
            </Button>
            {/* 개정사항 표 삽입 버튼 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('개정사항', form, contentRef)}
              title={t('editor.amendmentTable')}
            >
              {t('editor.amendment')}
            </Button>
            {/* 이미지 삽입 버튼 */}
            <Button
              size="small"
              icon={<PictureOutlined />}
              onClick={() => setImageModalVisible(true)}
              title={t('editor.insertImage')}
            >
              {t('editor.image')}
            </Button>
          </div>


            <div
              ref={contentRef}
              contentEditable={true}
              className="editor-content"
              style={{
                height: '600px',
                maxHeight: '600px',
                border: '1px solid #d9d9d9',
                borderTop: 'none',
                borderRadius: '0 0 6px 6px',
                padding: '16px',
                fontFamily: 'monospace',
                fontSize: '16px',
                lineHeight: '1.6',
                outline: 'none',
                overflowY: 'auto',
                backgroundColor: '#ffffff'
              }}
              onInput={(e) => {
                const target = e.target as HTMLDivElement;
                lastContentRef.current = target.innerHTML;
              }}
              onBlur={(e) => {
                const target = e.target as HTMLDivElement;
                const newContent = target.innerHTML;
                if (newContent !== lastContentRef.current) {
                  lastContentRef.current = newContent;
                  form.setFieldValue('content', newContent);
                }
              }}
            />
          </Form.Item>
          


          {/* 첨부파일 섹션 */}
          <Divider orientation="left">{t('laws.attachments')}</Divider>
          
          {law && (
            <div style={{ marginBottom: 16 }}>
              <Upload
                beforeUpload={handleFileUpload}
                showUploadList={false}
                disabled={uploading}
              >
                <Button 
                  icon={<UploadOutlined />} 
                  loading={uploading}
                  disabled={!law.law_id}
                >
                  {t('laws.uploadAttachment')}
                </Button>
              </Upload>
              
              {attachments.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <List
                    dataSource={attachments}
                    renderItem={(attachment) => (
                      <List.Item
                        actions={[
                          <Button
                            key="download"
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() => handleFileDownload(attachment)}
                            size="small"
                          >
                            {t('common.download')}
                          </Button>,
                          <Button
                            key="delete"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleFileRemove(attachment)}
                            size="small"
                          >
                            {t('common.delete')}
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<FileOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                          title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{decodeURIComponent(attachment.file_name)}</span>
                              <Tag>{attachmentService.formatFileSize(attachment.file_size)}</Tag>
                            </div>
                          }
                          description={`${t('laws.uploaded')}: ${new Date(attachment.uploaded_at).toLocaleString()}`}
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </div>
          )}
        </Form>
      </Card>

      {/* 표 삽입 모달 */}
      <Modal
        title={t('editor.insertTable')}
        open={tableModalVisible}
        onOk={handleInsertTable}
        onCancel={() => setTableModalVisible(false)}
        okText={t('editor.insert')}
        cancelText={t('common.cancel')}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.rowCount')}:</label>
          <InputNumber
            min={1}
            max={20}
            value={tableRows}
            onChange={(value) => setTableRows(value || 2)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('editor.columnCount')}:</label>
          <InputNumber
            min={1}
            max={10}
            value={tableCols}
            onChange={(value) => setTableCols(value || 2)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          {t('editor.tableInsertNote')}
        </div>
      </Modal>

      {/* 이미지 크기 조절 모달 */}
      <Modal
        title={t('editor.resizeImage')}
        open={imageResizeModalVisible}
        onOk={handleImageResize}
        onCancel={() => {
          setImageResizeModalVisible(false);
          setSelectedImage(null);
        }}
        width={500}
        footer={[
          <Button key="auto" onClick={handleAutoResize}>
            {t('editor.autoResize')}
          </Button>,
          <Button key="cancel" onClick={() => {
            setImageResizeModalVisible(false);
            setSelectedImage(null);
          }}>
            {t('common.cancel')}
          </Button>,
          <Button key="ok" type="primary" onClick={handleImageResize}>
            {t('editor.apply')}
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label={t('editor.currentImage')}>
            {selectedImage && (
              <div style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '8px', 
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <img
                  src={selectedImage.src}
                  alt={t('editor.selectedImage')}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('editor.width')}>
                <InputNumber
                  value={imageWidth}
                  onChange={(value) => setImageWidth(value || 300)}
                  min={50}
                  max={800}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('editor.height')}>
                <InputNumber
                  value={imageHeight}
                  onChange={(value) => setImageHeight(value || 200)}
                  min={50}
                  max={600}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
            {t('editor.autoResizeNote')}
          </div>
        </Form>
      </Modal>

      {/* 이미지 삽입 모달 */}
      <Modal
        title={t('editor.insertImage')}
        open={imageModalVisible}
        onOk={handleInsertImage}
        onCancel={() => {
          setImageModalVisible(false);
          setImageUrl('');
          setImageFile(null);
          setImagePreview('');
        }}
        width={600}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('editor.imageUrl')}>
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder={t('editor.imageUrlPlaceholder')}
                disabled={!!imageFile}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('editor.imageFile')}>
              <Upload
                beforeUpload={(file) => {
                  handleImageFileChange(file);
                  return false;
                }}
                showUploadList={false}
                accept="image/*"
                disabled={!!imageUrl.trim()}
              >
                <Button disabled={!!imageUrl.trim()}>
                  {t('editor.selectFile')}
                </Button>
              </Upload>
              {imageFile && (
                <div style={{ marginTop: 8 }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {imageFile.name}
                  </span>
                  <Button
                    type="text"
                    size="small"
                    onClick={handleImageFileRemove}
                    style={{ marginLeft: 8 }}
                  >
                    {t('editor.remove')}
                  </Button>
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* 이미지 미리보기 */}
        {(imagePreview || imageUrl) && (
          <Form.Item label={t('editor.preview')}>
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px', 
              padding: '8px', 
              textAlign: 'center',
              backgroundColor: '#fafafa'
            }}>
              <img
                src={imagePreview || imageUrl}
                alt={t('editor.preview')}
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  objectFit: 'contain'
                }}
              />
            </div>
          </Form.Item>
        )}
      </Modal>
    </div>
  );
};

export default LawEdit; 